import { createContext, useContext, useReducer, ReactNode } from 'react'

// 支付中心参数类型定义
interface PayCenterParams {
  onOk?: () => void
  type?: number | number[] // 指定类型，支持单个数字或数组
  quantities?: Record<number, number> // 指定数量 {type: quantity}
  [key: string]: any
}

// 支付中心状态类型定义
interface PayCenterState {
  showPayCenter: boolean
  params: PayCenterParams
}

// 支付中心动作类型
type PayCenterAction = { type: 'SHOW_PAY_CENTER'; params?: PayCenterParams } | { type: 'HIDE_PAY_CENTER' }

// 初始状态
const initialState: PayCenterState = {
  showPayCenter: false,
  params: {}
}

// Reducer
const payCenterReducer = (state: PayCenterState, action: PayCenterAction): PayCenterState => {
  switch (action.type) {
    case 'SHOW_PAY_CENTER':
      return {
        ...state,
        showPayCenter: true,
        params: action.params || {}
      }
    case 'HIDE_PAY_CENTER':
      return {
        ...state,
        showPayCenter: false,
        params: {}
      }
    default:
      return state
  }
}

// Context
const PayCenterContext = createContext<{
  state: PayCenterState
  dispatch: React.Dispatch<PayCenterAction>
} | null>(null)

// Provider组件
export const PayCenterProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(payCenterReducer, initialState)

  return <PayCenterContext.Provider value={{ state, dispatch }}>{children}</PayCenterContext.Provider>
}

// Hook
export const usePayCenterContext = () => {
  const context = useContext(PayCenterContext)
  if (!context) {
    throw new Error('usePayCenterContext must be used within a PayCenterProvider')
  }
  return context
}

// 便捷的Hook
export const usePayCenter = () => {
  const { state, dispatch } = usePayCenterContext()

  const showPayCenter = (params?: PayCenterParams) => {
    dispatch({ type: 'SHOW_PAY_CENTER', params })
  }

  const hidePayCenter = () => {
    dispatch({ type: 'HIDE_PAY_CENTER' })
  }

  return {
    showPayCenter,
    hidePayCenter,
    isPayCenterVisible: state.showPayCenter,
    params: state.params
  }
}
